import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { aperturaStore } from '../stores/aperturaStore';
import { authStore } from '../stores/authStore';
import { consultaStore } from '../stores/consultaStore';

import {
    Anexo,
    CargosResponse,
    ConsultaImpuestosResponse,
    ConsultaMensualResponse,
    ConsultaUsuariosResponse,
    Municipio,
    OrganismoOperador,
    RegistroEnviardoResponse,
    Reintegro,
    RolesResponse,
    UsuarioPassword,
    ConsultaAperturaResponse,
    ConsultaApertura
} from '../models';
import {
    AguaInformacion,
    AperturaRegistroImpuestosResponse,
    Notification,
    PredialInformacion,
    RegistroImpuestosAgua,
    RegistroImpuestosPredial
} from '../models';

import { Accion, Comentario, Usuario } from '../models';
import { tokenStore } from '../stores/tokenStore';
import { UsuarioAlta } from '../models/Altas';

export class Request {
    private httpClient: AxiosInstance;

    constructor() {
        this.httpClient = axios.create({
            baseURL: process.env.REACT_APP_IMPUESTOS_ENDPOINT
        });
    }

    get withToken() {
        this.httpClient.defaults.headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokenStore.token}`,
            pragma: 'no-cache',
            'cache-control': 'no-store'
        };
        return this.httpClient;
    }

    get withTokenCors() {
        this.httpClient.defaults.headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokenStore.token}`,
            pragma: 'no-cache',
            'cache-control': 'no-store',
            'Access-Control-Allow-Origin': '*'
        };
        return this.httpClient;
    }

    get validationWithToken() {
        this.httpClient.defaults.headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokenStore.token}`,
            pragma: 'no-cache',
            'cache-control': 'no-store'
        };
        return this.httpClient;
    }

    get sinToken() {
        this.httpClient.defaults.headers = {
            'Content-Type': 'application/json',
            pragma: 'no-cache',
            'cache-control': 'no-store'
        };
        return this.httpClient;
    }

    // PUT /api/notificacion/{notificacionId}/
    public async putNotificaciones(
        endpoint: string,
        data: Notification
    ): Promise<AxiosResponse<{}>> {
        const params: AxiosRequestConfig = {
            baseURL: process.env.REACT_APP_NOTIFICACIONES_ENDPOINT
        };
        return await this.withToken.put(endpoint, data, params);
    }

    // POST /api/notificacion/nuevo
    public async postNotificacion(
        endpoint: string,
        params: any
    ): Promise<AxiosResponse<Notification>> {
        const baseURL = process.env.REACT_APP_NOTIFICACIONES_ENDPOINT;
        return await this.withToken.post(endpoint, null, { baseURL, params: { ...params } });
    }

    // DELETE /api/notificacion/notification/{notificationId}
    public async deleteNotification(endpoint: string): Promise<AxiosResponse<{}>> {
        const params: AxiosRequestConfig = {
            baseURL: process.env.REACT_APP_NOTIFICACIONES_ENDPOINT
        };
        return await this.withToken.delete(endpoint, params);
    }

    // put /api/notificacion/{notificationId}/archivar
    public async archivarNotification(endpoint: string): Promise<AxiosResponse<{}>> {
        const params: AxiosRequestConfig = {
            baseURL: process.env.REACT_APP_NOTIFICACIONES_ENDPOINT
        };
        return await this.withToken.put(endpoint, null, params);
    }

    // GET /api/notificacion/ejemplo/archivo/fileName
    public async getArchivo(endpoint: string): Promise<AxiosResponse<string>> {
        const params: AxiosRequestConfig = {
            baseURL: process.env.REACT_APP_NOTIFICACIONES_ENDPOINT,
            responseType: 'arraybuffer'
        };
        return await this.withToken.get(endpoint, params);
    }

    // GET /api/reportes/reporte
    public async getReportePdf(endpoint: string, parametros: any): Promise<AxiosResponse<string>> {
        const params: AxiosRequestConfig = {
            baseURL: process.env.REACT_APP_REPORTES_ENDPOINT,
            responseType: 'arraybuffer',
            params: parametros
        };
        return await this.withToken.get(endpoint, params);
    }

    // GET /api/reportes/reporte
    public async getReportePdfSinBuffer(
        endpoint: string,
        parametros: any
    ): Promise<AxiosResponse<string>> {
        const params: AxiosRequestConfig = {
            baseURL: process.env.REACT_APP_REPORTES_ENDPOINT,
            params: parametros
        };
        return await this.withToken.get(endpoint, params);
    }

    // DELETE /api/notificacion/ejemplo/archivo/fileName
    public async deleteArchivo(endpoint: string): Promise<AxiosResponse<{}>> {
        const params: AxiosRequestConfig = {
            baseURL: process.env.REACT_APP_NOTIFICACIONES_ENDPOINT
        };
        return await this.withToken.delete(endpoint, params);
    }

    // GET /api/registroimpuestos{predial | agua}/{formid}
    public async get(
        endpoint: string,
        registroImpuestosId: number
    ): Promise<AxiosResponse<RegistroImpuestosPredial | RegistroImpuestosAgua>> {
        const params: AxiosRequestConfig = { params: { registroImpuestosId } };
        return await this.withToken.get(endpoint, params);
    }

    // GET /api/estado/enviado
    public async getEnviado(
        endpoint: string,
        params: any
    ): Promise<AxiosResponse<RegistroEnviardoResponse>> {
        return await this.withToken.get(endpoint, {
            baseURL: process.env.REACT_APP_IMPUESTOS_ENDPOINT,
            params
        });
    }

    // GET /api/estado/consulta
    public async getConsulta(
        endpoint: string,
        params: any
    ): Promise<AxiosResponse<ConsultaImpuestosResponse>> {
        return await this.withToken.get(endpoint, {
            baseURL: process.env.REACT_APP_REPORTES_ENDPOINT,
            params
        });
    }

    // GET /api/seguridad/usuario
    public async getUsuario(endpoint: string): Promise<AxiosResponse<Usuario>> {
        return await this.withToken.get(endpoint, {
            // temporary for demo since api/seguridad/usuario is not deployed yet
            baseURL: process.env.REACT_APP_SEGURIDAD_ENDPOINT
        });
    }

    // GET /api/seguridad/Acciones
    public async getAcciones(endpoint: string): Promise<AxiosResponse<Accion[]>> {
        return await this.withToken.get(endpoint, {
            baseURL: process.env.REACT_APP_SEGURIDAD_ENDPOINT
        });
    }

    // GET /api/notificacion/notificacion/usuario
    public async getNotificacionUsuario(
        endpoint: string,
        params: any
    ): Promise<AxiosResponse<Notification[]>> {
        return await this.withToken.get(endpoint, {
            baseURL: process.env.REACT_APP_NOTIFICACIONES_ENDPOINT,
            params
        });
    }

    // GET /api/municipio
    public async getMunicipio(
        endpoint: string
    ): Promise<AxiosResponse<Municipio[] | OrganismoOperador[]>> {
        return await this.withToken.get(endpoint, {
            baseURL: process.env.REACT_APP_MUNICIPIO_ENDPOINT
        });
    }

    // GET /api/registroimpuesto{predial | agua}
    public async getNext(
        endpoint: string,
        municipioId: number
    ): Promise<AxiosResponse<AperturaRegistroImpuestosResponse>> {
        const params: AxiosRequestConfig = { params: { municipioId } };
        return await this.withToken.get(endpoint, params);
    }

    // POST /api/registroimpuesto{predial | agua}
    public async post(
        endpoint: string,
        municipioId: number
    ): Promise<AxiosResponse<RegistroImpuestosPredial | RegistroImpuestosAgua>> {
        const params: AxiosRequestConfig = {
            params: {
                ejercicio: aperturaStore.ejercicio,
                mes: aperturaStore.mes,
                municipioId
            }
        };
        return await this.withToken.post(endpoint, null, params);
    }

    // 1: PUT /api/registroimpuestos{predial | agua}/{formid}/{ingresos | ingresosporuso | cuentas | descuentos}
    // 2: PUT /api/registroimpuestos{predial | agua}/{formid}/estado/enviado
    public async put(
        endpoint: string,
        body:
            | AguaInformacion
            | PredialInformacion
            | {
                  comentario: string;
              }
    ): Promise<AxiosResponse<null | Comentario>> {
        return await this.withToken.put(endpoint, body);
    }

    // GET /api/reportes/ingres
    public async getIngres(endpoint: string): Promise<AxiosResponse<string>> {
        return await this.sinToken.get(endpoint, {
            baseURL: process.env.REACT_APP_REPORTES_ENDPOINT
        });
    }
    // GET /api/municipio
    public async getReintegro(endpoint: string): Promise<AxiosResponse<Reintegro>> {
        const response = await this.withToken.get(endpoint, {
            baseURL: process.env.REACT_APP_MUNICIPIO_ENDPOINT
        });
        return response;
    }

    // GET /api/estado/consulta
    public async getConsultaMensual(
        endpoint: string,
        params: any
    ): Promise<AxiosResponse<ConsultaMensualResponse>> {
        return await this.withToken.get(endpoint, {
            baseURL: process.env.REACT_APP_REPORTES_ENDPOINT,
            params
        });
    }

    // GET /api/seguridad/validar
    public async getToken(endpoint: string, params: any): Promise<AxiosResponse<string>> {
        return await this.sinToken.get(endpoint, {
            baseURL: process.env.REACT_APP_SEGURIDAD_ENDPOINT,
            params
        });
    }

    // GET /api/seguridad/usuario
    public async validaTokenUsuario(endpoint: string): Promise<AxiosResponse<Usuario>> {
        return await this.validationWithToken.get(endpoint, {
            // temporary for demo since api/seguridad/usuario is not deployed yet
            baseURL: process.env.REACT_APP_SEGURIDAD_ENDPOINT
        });
    }

    // GET /api/seguridad/cambiarPassword
    public async updatePassword(
        endpoint: string,
        body: UsuarioPassword
    ): Promise<AxiosResponse<string>> {
        const params: AxiosRequestConfig = {
            baseURL: process.env.REACT_APP_SEGURIDAD_ENDPOINT
        };
        return await this.withTokenCors.put(endpoint, body, params);
    }
    // GET /api/reportes/obtenerAnexos
    public async getAnexos(endpoint: string, params: any): Promise<AxiosResponse<Anexo[]>> {
        return await this.withToken.get(endpoint, {
            baseURL: process.env.REACT_APP_REPORTES_ENDPOINT,
            params
        });
    }

    // GET /api/reportes/reporteExcel
    public async getExcelAnexo(endpoint: string, parametros: any): Promise<AxiosResponse<string>> {
        {
            const params: AxiosRequestConfig = {
                baseURL: process.env.REACT_APP_REPORTES_ENDPOINT,
                responseType: 'arraybuffer',
                params: parametros
            };
            return await this.withToken.get(endpoint, params);
        }
    }

    // GET /api/reportes/ReporteMensualExcel
    public async getReporteMensualExcel(
        endpoint: string,
        parametros: any
    ): Promise<AxiosResponse<string>> {
        {
            const params: AxiosRequestConfig = {
                baseURL: process.env.REACT_APP_REPORTES_ENDPOINT,
                responseType: 'arraybuffer',
                params: parametros
            };
            return await this.withToken.get(endpoint, params);
        }
    }

    // GET /api/reportes/reportenotificacionlectura
    public async getReporteNotificacion(
        endpoint: string,
        parametros: any
    ): Promise<AxiosResponse<string>> {
        const params: AxiosRequestConfig = {
            baseURL: process.env.REACT_APP_REPORTES_ENDPOINT,
            responseType: 'arraybuffer',
            params: parametros
        };
        return await this.withToken.get(endpoint, params);
    }

    // GET /api/reportes/listaUsuarios
    public async getConsultaUsuarios(
        endpoint: string,
        params: any
    ): Promise<AxiosResponse<ConsultaUsuariosResponse>> {
        return await this.withToken.get(endpoint, {
            baseURL: process.env.REACT_APP_REPORTES_ENDPOINT,
            params
        });
    }

    // GET /api/reportes/obtenerroles
    public async getConsultaRoles(endpoint: string): Promise<AxiosResponse<RolesResponse>> {
        return await this.withToken.get(endpoint, {
            baseURL: process.env.REACT_APP_SEGURIDAD_ENDPOINT
        });
    }

    // GET /api/reportes/reportenotificacionlectura
    public async getResponsiva(endpoint: string, parametros: any): Promise<AxiosResponse<string>> {
        const params: AxiosRequestConfig = {
            baseURL: process.env.REACT_APP_REPORTES_ENDPOINT,
            responseType: 'arraybuffer',
            params: parametros
        };
        return await this.withToken.get(endpoint, params);
    }

    // GET /api/reportes/obtenercargos
    public async getConsultaCargos(endpoint: string): Promise<AxiosResponse<CargosResponse>> {
        return await this.withToken.get(endpoint, {
            baseURL: process.env.REACT_APP_SEGURIDAD_ENDPOINT
        });
    }

    // POST /api/seguridad/usuario
    public async postUsuario(endpoint: string, params: any): Promise<AxiosResponse<string>> {
        const baseURL = process.env.REACT_APP_SEGURIDAD_ENDPOINT;
        return await this.withToken.post(endpoint, null, { baseURL, params: { ...params } });
    }

    // GET /api/seguridad/obtenerusuarioconsulta
    public async getUsuarioConsulta(
        endpoint: string,
        parametros: any
    ): Promise<AxiosResponse<Usuario>> {
        return await this.withToken.get(endpoint, {
            baseURL: process.env.REACT_APP_SEGURIDAD_ENDPOINT,
            params: parametros
        });
    }

    // GET /api/seguridad/restablecerpassword
    public async putRestablecerPassword(
        endpoint: string,
        parametros: any
    ): Promise<AxiosResponse<{}>> {
        const params: AxiosRequestConfig = {
            baseURL: process.env.REACT_APP_SEGURIDAD_ENDPOINT,
            params: parametros
        };
        return await this.withToken.put(endpoint, null, params);
    }

    // PUT /api/seguridad/editarusuario
    public async postEditarUsuario(endpoint: string, params: any): Promise<AxiosResponse<string>> {
        const baseURL = process.env.REACT_APP_SEGURIDAD_ENDPOINT;
        return await this.withToken.put(endpoint, null, { baseURL, params: { ...params } });
    }

    // PUT /api/seguridad/eliminarusuario
    public async postEliminarUsuario(
        endpoint: string,
        params: any
    ): Promise<AxiosResponse<string>> {
        const baseURL = process.env.REACT_APP_SEGURIDAD_ENDPOINT;
        return await this.withToken.put(endpoint, null, { baseURL, params: { ...params } });
    }

    // GET /api/reportes/reporteanual
    public async getReporteAnualPdf(
        endpoint: string,
        parametros: any
    ): Promise<AxiosResponse<string>> {
        const params: AxiosRequestConfig = {
            baseURL: process.env.REACT_APP_REPORTES_ENDPOINT,
            responseType: 'arraybuffer',
            params: parametros
        };
        return await this.withToken.get(endpoint, params);
    }

    // GET /api/reportes/reporteanual
    public async getReporteAnualSinBuffer(
        endpoint: string,
        parametros: any
    ): Promise<AxiosResponse<string>> {
        const params: AxiosRequestConfig = {
            baseURL: process.env.REACT_APP_REPORTES_ENDPOINT,
            params: parametros
        };
        return await this.withToken.get(endpoint, params);
    }

    // GET /api/municipio
    public async getMunicipiosSoapap(endpoint: string): Promise<AxiosResponse<Municipio[]>> {
        return await this.withToken.get(endpoint, {
            baseURL: process.env.REACT_APP_MUNICIPIO_ENDPOINT
        });
    }

    // GET /api/registroimpuesto/obtenerAperturas
    public async getAperturas(
        endpoint: string,
        parametros: any
    ): Promise<AxiosResponse<ConsultaAperturaResponse>> {
        const params: AxiosRequestConfig = {
            baseURL: process.env.REACT_APP_IMPUESTOS_ENDPOINT,
            params: parametros
        };
        return await this.withToken.get(endpoint, params);
    }

    // post /api/registroimpuesto/insertaraperturas
    public async postInsertarAperturas(
        endpoint: string,
        params: any
    ): Promise<AxiosResponse<string>> {
        const baseURL = process.env.REACT_APP_IMPUESTOS_ENDPOINT;
        return await this.withToken.post(endpoint, null, { baseURL, data: { ...params } });
    }
}
